import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';

const WarehouseMap = ({ warehouseId, highlightSku = '' }) => {
    const { user } = useAuth();
    const { showToast } = useUI();
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedElement, setSelectedElement] = useState(null);
    const [hoveredId, setHoveredId] = useState(null);

    useEffect(() => {
        const fetchMapData = async () => {
            try {
                const res = await fetch(`/api/locations?warehouseId=${warehouseId}&companyId=${user.companyId}&includeProducts=true`);
                if (res.ok) setLocations(await res.json());
            } catch (error) {
                showToast("Erreur chargement carte", "error");
            } finally {
                setLoading(false);
            }
        };
        fetchMapData();
    }, [warehouseId, user.companyId, showToast]);

    // Build hierarchy and auto-layout
    const layout = useMemo(() => {
        if (locations.length === 0) return { zones: [], totalW: 800, totalH: 500 };

        const zones = locations.filter(l => l.type === 'Zone');
        const aisles = locations.filter(l => l.type === 'Aisle');
        const elements = locations.filter(l => l.type === 'Element');
        const slots = locations.filter(l => l.type === 'Slot');

        const query = (highlightSku || '').toLowerCase().trim();

        // Auto-layout constants
        const ZONE_PAD = 35;
        const ZONE_GAP = 50;
        const AISLE_W = 130;
        const AISLE_GAP = 35;
        const AISLE_HEADER = 50;
        const ELEM_H = 100;
        const ELEM_GAP = 20;
        const SLOT_H = 32;
        const SLOT_GAP = 8;

        let layoutZones = [];
        let curX = 40;


        zones.forEach(zone => {
            const zoneAisles = aisles.filter(a => a.parentId === zone.id);
            let aisleX = ZONE_PAD;
            let maxAisleH = 80;

            let layoutAisles = [];

            zoneAisles.forEach(aisle => {
                const aisleElements = elements.filter(e => e.parentId === aisle.id);
                let elemY = AISLE_HEADER + 10;
                let layoutElements = [];

                    aisleElements.forEach(elem => {
                        const elemSlots = slots.filter(s => s.parentId === elem.id);
                        const elemHeight = Math.max(ELEM_H, 40 + elemSlots.length * (SLOT_H + SLOT_GAP));


                        let layoutSlots = elemSlots.map((slot, si) => {
                            const slotHasProduct = query && (slot.products || []).some(p => 
                                (p.sku || '').toLowerCase().includes(query) || 
                                (p.name || '').toLowerCase().includes(query)
                            );
                            return {
                                ...slot,
                                rx: 8,
                                ry: 36 + si * (SLOT_H + SLOT_GAP),
                                rw: AISLE_W - 24,
                                rh: SLOT_H,
                                highlighted: !!slotHasProduct
                            };
                        });

                        const elemHasProduct = query && (
                            (elem.products || []).some(p => 
                                (p.sku || '').toLowerCase().includes(query) || 
                                (p.name || '').toLowerCase().includes(query)
                            ) ||
                            layoutSlots.some(s => s.highlighted)
                        );


                        layoutElements.push({
                            ...elem,
                            rx: 6,
                            ry: elemY,
                        rw: AISLE_W - 12,
                        rh: elemHeight,

                        slots: layoutSlots,
                        highlighted: !!elemHasProduct
                    });


                    elemY += elemHeight + ELEM_GAP;
                });

                const totalAisleH = Math.max(80, elemY + 10);
                if (totalAisleH > maxAisleH) maxAisleH = totalAisleH;

                layoutAisles.push({
                    ...aisle,
                    rx: aisleX,
                    ry: 0,
                    rw: AISLE_W,
                    rh: totalAisleH,
                    elements: layoutElements
                });

                aisleX += AISLE_W + AISLE_GAP;
            });

            const zoneW = Math.max(250, aisleX + ZONE_PAD);
            const zoneH = maxAisleH + 100;


            // Update aisle ry offsets
            layoutAisles = layoutAisles.map(a => ({
                ...a,
                ry: 55 // header zone

            }));

            layoutZones.push({
                ...zone,
                rx: curX,
                ry: 40,

                rw: zoneW,
                rh: zoneH,
                aisles: layoutAisles
            });

            curX += zoneW + ZONE_GAP;
        });

        const totalW = Math.max(800, curX + 30);
        const totalH = Math.max(500, Math.max(...layoutZones.map(z => z.ry + z.rh + 30), 500));

        return { zones: layoutZones, totalW, totalH };
    }, [locations, highlightSku]);

    if (loading) return <div className="h-full flex items-center justify-center text-slate-400 animate-pulse">Génération du plan...</div>;

    if (locations.length === 0) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 p-12 text-center">
                <span className="material-symbols-outlined text-4xl mb-4 text-slate-200">account_tree</span>
                <p className="font-medium">Aucun emplacement configuré.</p>
                <p className="text-xs mt-1">Utilisez le bouton "Emplacements" sur la carte d'entrepôt pour créer des Zones, Allées, et Racks.</p>
            </div>
        );
    }

    const typeColors = {
        Zone: { fill: '#f1f5f9', stroke: '#cbd5e1', text: '#64748b' },
        Aisle: { fill: '#ffffff', stroke: '#e2e8f0', text: '#475569' },
        Element: { fill: '#eff6ff', stroke: '#bfdbfe', text: '#1e40af' },
        Slot: { fill: '#f0fdf4', stroke: '#bbf7d0', text: '#166534' }
    };

    return (
        <div className="relative h-full w-full flex overflow-hidden bg-slate-50/50 dark:bg-slate-950/30">
            <div className="flex-1 overflow-auto p-4 custom-scrollbar">
                <svg
                    viewBox={`0 0 ${layout.totalW} ${layout.totalH}`}
                    width={layout.totalW}
                    height={layout.totalH}
                    className="min-w-full"
                >
                    {/* Background grid */}
                    <defs>
                        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e2e8f0" strokeWidth="0.3" opacity="0.5" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#grid)" rx="12" />

                    {/* Zones */}
                    {layout.zones.map(zone => (
                        <g key={zone.id}>
                            {/* Zone background */}
                            <rect
                                x={zone.rx} y={zone.ry} width={zone.rw} height={zone.rh}
                                rx="10"
                                fill={typeColors.Zone.fill}
                                stroke={typeColors.Zone.stroke}
                                strokeWidth="1.5"
                                strokeDasharray="6 3"
                            />
                            <text x={zone.rx + 16} y={zone.ry + 32} fontSize="18" fontWeight="900" fill={typeColors.Zone.text}>
                                {zone.name}
                            </text>
                            <text x={zone.rx + zone.rw - 16} y={zone.ry + 32} fontSize="12" fontWeight="700" fill="#94a3b8" textAnchor="end">

                                ZONE
                            </text>

                            {/* Aisles */}
                            {zone.aisles.map(aisle => (
                                <g key={aisle.id} transform={`translate(${zone.rx + aisle.rx}, ${zone.ry + aisle.ry})`}>
                                    {/* Aisle background */}
                                    <rect
                                        x={0} y={0} width={aisle.rw} height={aisle.rh}
                                        rx="6"
                                        fill={typeColors.Aisle.fill}
                                        stroke={typeColors.Aisle.stroke}
                                        strokeWidth="1"
                                    />
                                    {/* Aisle label */}
                                    <rect x={0} y={0} width={aisle.rw} height={34} rx="6" fill="#f8fafc" />
                                    <text x={aisle.rw / 2} y={24} fontSize="15" fontWeight="800" fill={typeColors.Aisle.text} textAnchor="middle">

                                        {aisle.name}
                                    </text>

                                    {/* Elements (Racks) */}
                                    {aisle.elements.map(elem => (
                                        <g
                                            key={elem.id}
                                            className="cursor-pointer"
                                            onClick={() => setSelectedElement(selectedElement?.id === elem.id ? null : elem)}
                                            onMouseEnter={() => setHoveredId(elem.id)}
                                            onMouseLeave={() => setHoveredId(null)}
                                        >
                                            <rect
                                                x={elem.rx} y={elem.ry} width={elem.rw} height={elem.rh}
                                                rx="4"
                                                fill={elem.highlighted ? '#fef2f2' : hoveredId === elem.id ? '#dbeafe' : typeColors.Element.fill}
                                                stroke={elem.highlighted ? '#ef4444' : hoveredId === elem.id ? '#3b82f6' : typeColors.Element.stroke}
                                                strokeWidth={elem.highlighted ? 3 : hoveredId === elem.id ? 1.5 : 1}
                                            />
                                            {elem.highlighted && (
                                                <rect
                                                    x={elem.rx} y={elem.ry} width={elem.rw} height={elem.rh}
                                                    rx="4"
                                                    fill="none"
                                                    stroke="#ef4444"
                                                    strokeWidth="2"
                                                    opacity="0.5"
                                                >
                                                    <animate attributeName="opacity" values="0.3;1;0.3" dur="1.5s" repeatCount="indefinite" />
                                                </rect>
                                            )}
                                            <text x={elem.rx + elem.rw / 2} y={elem.ry + 24} fontSize="12" fontWeight="900" fill={elem.highlighted ? '#dc2626' : typeColors.Element.text} textAnchor="middle">

                                                {elem.name}
                                            </text>

                                            {/* Slots */}
                                            {elem.slots.map(slot => (
                                                <rect
                                                    key={slot.id}
                                                    x={elem.rx + slot.rx}
                                                    y={elem.ry + slot.ry}
                                                    width={slot.rw}
                                                    height={slot.rh}
                                                    rx="2"
                                                    fill={slot.highlighted ? '#fef2f2' : typeColors.Slot.fill}
                                                    stroke={slot.highlighted ? '#ef4444' : typeColors.Slot.stroke}
                                                    strokeWidth={slot.highlighted ? 2 : 0.5}
                                                >
                                                    {slot.highlighted && (
                                                        <animate attributeName="opacity" values="0.5;1;0.5" dur="1.2s" repeatCount="indefinite" />
                                                    )}
                                                </rect>
                                            ))}

                                            {elem.slots.map(slot => (
                                                <text
                                                    key={`t-${slot.id}`}
                                                    x={elem.rx + slot.rx + slot.rw / 2}
                                                    y={elem.ry + slot.ry + 10}
                                                    fontSize="9"

                                                    fontWeight="600"
                                                    fill={typeColors.Slot.text}
                                                    textAnchor="middle"
                                                >
                                                    {slot.name}
                                                </text>
                                            ))}
                                        </g>
                                    ))}
                                </g>
                            ))}
                        </g>
                    ))}
                </svg>
            </div>

            {/* Floating Side Panel for Selected Element */}
            {selectedElement && (
                <div className="w-80 border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 z-10">
                    <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-blue-50/50 dark:bg-blue-900/10">
                        <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-blue-600">inventory_2</span>
                            <span className="font-black text-blue-700 dark:text-blue-300 uppercase text-sm tracking-tight">{selectedElement.name}</span>
                        </div>
                        <button onClick={() => setSelectedElement(null)} className="text-slate-400 hover:text-rose-500 p-1">
                            <span className="material-symbols-outlined text-xl">close</span>
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                        {(() => {
                            const ownProducts = selectedElement.products || [];
                            const slotLocations = locations.filter(l => l.parentId === selectedElement.id && l.type === 'Slot');
                            
                            const sections = [];
                            
                            if (ownProducts.length > 0) {
                                sections.push(
                                    <div key="base" className="space-y-2">
                                        <div className="flex items-center gap-2 px-1">
                                            <div className="w-1.5 h-4 bg-primary rounded-full"></div>
                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Sur le Rack</span>
                                        </div>
                                        <div className="grid gap-2">
                                            {ownProducts.map(p => (
                                                <div key={p.id} className="p-3 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl shadow-sm flex justify-between items-center group hover:border-primary/30 transition-colors">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-bold text-slate-900 dark:text-white text-xs truncate">{p.name}</p>
                                                        <p className="text-[10px] text-slate-400 font-bold tracking-tighter uppercase">{p.sku}</p>
                                                    </div>
                                                    <div className="bg-primary/10 text-primary px-2.5 py-1 rounded-lg font-black text-xs">
                                                        {p.quantity}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            }
                            
                            slotLocations.forEach(slot => {
                                const slotProds = slot.products || [];
                                if (slotProds.length > 0) {
                                    sections.push(
                                        <div key={slot.id} className="space-y-2">
                                            <div className="flex items-center gap-2 px-1">
                                                <div className="w-1.5 h-4 bg-emerald-500 rounded-full"></div>
                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Emplacement : {slot.name}</span>
                                            </div>
                                            <div className="grid gap-2">
                                                {slotProds.map(p => (
                                                    <div key={p.id} className="p-3 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl shadow-sm flex justify-between items-center group hover:border-emerald-500/30 transition-colors">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-bold text-slate-900 dark:text-white text-xs truncate">{p.name}</p>
                                                            <p className="text-[10px] text-slate-400 font-bold tracking-tighter uppercase">{p.sku}</p>
                                                        </div>
                                                        <div className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 px-2.5 py-1 rounded-lg font-black text-xs border border-emerald-100 dark:border-emerald-800">
                                                            {p.quantity}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }
                            });
                            
                            if (sections.length === 0) return (
                                <div className="h-full flex flex-col items-center justify-center text-slate-300 py-12">
                                    <span className="material-symbols-outlined text-4xl mb-2">inventory</span>
                                    <p className="text-xs font-bold uppercase tracking-wider">Aucun produit ici</p>
                                </div>
                            );
                            return sections;
                        })()}
                    </div>

                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800">
                        <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                            <span>Légende</span>
                            <span>{locations.filter(l => l.parentId === selectedElement.id).length} Slots</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <span className="flex items-center gap-1 text-[9px] font-bold text-slate-500"><div className="w-2 h-2 rounded bg-primary"></div> Rack</span>
                            <span className="flex items-center gap-1 text-[9px] font-bold text-slate-500"><div className="w-2 h-2 rounded bg-emerald-500"></div> Slot</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Info Badge (Floating) */}
            {!selectedElement && (
                <div className="absolute bottom-6 right-6 p-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl max-w-xs animate-in fade-in slide-in-from-bottom-4 duration-500 pointer-events-none">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                            <span className="material-symbols-outlined">info</span>
                        </div>
                        <div>
                            <p className="text-xs font-black text-slate-900 dark:text-white uppercase tracking-tight">Mode Interactif</p>
                            <p className="text-[10px] text-slate-500 font-medium leading-tight">Cliquez sur un rack pour explorer son contenu complet.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WarehouseMap;
